import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Download, Loader2, Mail, Phone } from "lucide-react";
import { downloadCallAuditCsv, getCallAnalytics, getCallInsight, listCallFollowups, listCallInsights, markCallFollowupDone } from "../lib/api";

const when = (iso) => { try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return ""; } };
const mins = (s) => (s ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}` : "—");
const pct = (v) => (v == null ? "—" : `${v}%`);
const money = (v) => (v == null ? "—" : `$${v.toFixed(2)}`);
const errText = (e, fallback) => e?.response?.data?.detail || fallback;

const VERDICT = {
  hot: { label: "Hot", cls: "badge-hot", help: "Likely to take the next step" },
  warm: { label: "Warm", cls: "badge-yellow", help: "Open to it, needs nurturing" },
  cool: { label: "Cool", cls: "badge-blue", help: "Polite but lukewarm" },
  not_interested: { label: "Not interested", cls: "", help: "Declined or asked to stop" },
  no_conversation: { label: "No conversation", cls: "", help: "Didn't answer or barely spoke" },
};
const OUTCOME = { callback_requested: "Call back agreed", demo_booked: "Meeting booked", send_info: "Wants info", not_interested: "Not interested",
  wrong_person: "Wrong person", voicemail: "Voicemail", do_not_call: "Do not call", no_conversation: "No conversation", unclassified: "Unclassified" };

export function FollowupsView() {
  const [rows, setRows] = useState(null);
  const [showDone, setShowDone] = useState(false);

  const load = async (d = showDone) => {
    try { setRows(await listCallFollowups(d)); } catch (e) { toast.error(errText(e, "Could not load follow-ups")); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const done = async (r) => { try { await markCallFollowupDone(r.id, !r.done); load(); window.dispatchEvent(new Event("call-followups-changed")); } catch { toast.error("Could not update"); } };

  return <div className="page-view" data-testid="followups-view">
    <div className="view-toolbar">
      <div><h2>Follow-ups</h2><p>People who agreed to a call back, a meeting, or information. Contact them at the time they asked for.</p></div>
      <div className="toolbar-actions"><label className="inline-check"><input type="checkbox" checked={showDone} onChange={(e) => { setShowDone(e.target.checked); load(e.target.checked); }} /> show completed</label></div>
    </div>
    {rows && rows.length === 0 && <div className="panel"><p>Nothing to follow up. When someone agrees to a call back or asks for information, they appear here.</p></div>}
    <div className="followup-list">
      {(rows || []).map(r => <div key={r.id} className={`panel followup ${r.done ? "is-done" : ""}`} data-testid={`followup-${r.id}`}>
        <div className="followup-head">
          <b>{r.action}</b>
          <span className={`badge ${VERDICT[r.interest]?.cls || ""}`}>{(VERDICT[r.interest]?.label || r.interest)}{r.score != null ? ` · ${r.score}` : ""}</span>
          <small className="muted">{r.name}{r.company ? ` · ${r.company}` : ""} · {when(r.when)}</small>
          <span className="followup-actions">
            {r.phone && <a className="outline-button" href={`tel:${r.phone}`}><Phone size={13} /> {r.phone}</a>}
            {r.email && <a className="outline-button" href={`mailto:${r.email}`}><Mail size={13} /> {r.email}</a>}
            <button className="primary-button" onClick={() => done(r)}><Check size={13} /> {r.done ? "Reopen" : "Done"}</button>
          </span>
        </div>
        {r.preference && <p><b>They asked for:</b> {r.preference}</p>}
        {r.next_step && <p><b>Agreed next step:</b> {r.next_step}</p>}
        {r.summary && <p>{r.summary}</p>}
        {r.notes && <p><small className="muted">Notes: {r.notes}</small></p>}
      </div>)}
    </div>
  </div>;
}

export function InsightsView() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [showEmpty, setShowEmpty] = useState(false);
  const [open, setOpen] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = async (empty = showEmpty) => {
    try { setData(await listCallInsights(empty)); } catch (e) { toast.error(errText(e, "Could not load insights")); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = async (id) => {
    if (open === id) { setOpen(null); return; }
    setOpen(id); setDetail(null);
    try { setDetail(await getCallInsight(id)); } catch { toast.error("Could not load that call"); }
  };
  const rows = (data?.calls || []).filter(r => filter === "all" || r.verdict === filter);

  return <div className="page-view" data-testid="insights-view">
    <div className="view-toolbar">
      <div><h2>Call insights</h2><p>Reads each finished call and judges how interested the person was, with the reasons shown.</p></div>
      <div className="toolbar-actions"><label className="inline-check"><input type="checkbox" checked={showEmpty} onChange={(e) => { setShowEmpty(e.target.checked); load(e.target.checked); }} /> show unanswered</label></div>
    </div>
    <div className="kpi-grid kpi-grid-5">
      {Object.entries(VERDICT).map(([k, v]) => <button key={k} className={`kpi kpi-button ${filter === k ? "on" : ""}`} onClick={() => setFilter(filter === k ? "all" : k)} data-testid={`insight-count-${k}`}>
        <div className="kpi-label">{v.label}</div><strong>{data?.counts?.[k] ?? "–"}</strong><small>{v.help}</small></button>)}
    </div>
    <div className="full-table panel">
      <table>
        <thead><tr><th>When</th><th>Who</th><th>Interest</th><th>Score</th><th>Why</th><th>Length</th></tr></thead>
        <tbody>
          {data && rows.length === 0 && <tr><td colSpan={6} className="empty-row">No calls to show yet. Once a call finishes it appears here.</td></tr>}
          {rows.map(r => {
            const v = VERDICT[r.verdict];
            return [
              <tr key={r.id} className="call-row" onClick={() => toggle(r.id)} data-testid={`insight-row-${r.id}`}>
                <td><small>{when(r.when)}</small></td>
                <td><b>{r.name}</b><small className="table-sub">{[r.company, r.number].filter(Boolean).join(" · ")}</small></td>
                <td><span className={`badge ${v.cls}`}>{v.label}</span>{r.confidence === "low" && <small className="table-sub">estimated</small>}</td>
                <td>{r.score ?? "—"}</td>
                <td className="call-summary-cell"><small>{r.reasons.slice(0, 2).join(" · ")}</small></td>
                <td><small>{mins(r.duration)}</small></td>
              </tr>,
              open === r.id && <tr key={r.id + "-d"}><td colSpan={6}>
                {!detail ? <Loader2 size={14} className="spin" /> : <div className="insight-detail">
                  <div><b>Summary</b><p>{detail.summary || "No summary for this call."}</p></div>
                  <div><b>Why this rating</b><ul>{detail.assessment.reasons.map(x => <li key={x}>{x}</li>)}</ul></div>
                  {detail.data?.next_step && <div><b>Next step:</b> {detail.data.next_step}</div>}
                  {detail.recording_url && <audio controls src={detail.recording_url} preload="none" />}
                  <div><b>Conversation</b><pre className="call-transcript">{detail.transcript || "No transcript."}</pre></div>
                </div>}
              </td></tr>,
            ];
          })}
        </tbody>
      </table>
    </div>
  </div>;
}

export function AnalyticsView() {
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  useEffect(() => { getCallAnalytics(days).then(setD).catch(() => toast.error("Could not load analytics")); }, [days]);
  if (!d) return <div className="panel">Loading…</div>;
  const maxDay = Math.max(1, ...d.series.map(s => s.calls));
  const maxOut = Math.max(1, ...Object.values(d.outcomes));
  const tiles = [
    ["Calls made", d.placed, `${d.connected} real conversations`],
    ["Connect rate", pct(d.connect_rate), "of calls became a conversation"],
    ["Call backs / meetings", d.callbacks_or_demos, `${pct(d.conversion_rate)} of conversations`],
    ["Avg talk time", d.avg_talk_seconds ? `${Math.floor(d.avg_talk_seconds / 60)}m ${d.avg_talk_seconds % 60}s` : "—", "connected calls only"],
    ["Spend", money(d.spend), "as reported by your voice provider"],
    ["Cost per result", money(d.cost_per_result), "per call back or meeting"],
  ];
  return <div className="page-view" data-testid="analytics-view">
    <div className="view-toolbar">
      <div><h2>Call analytics</h2><p>How the calling is performing{d.provider !== "all" ? ` on ${(d.providers.find(p => p.id === d.provider) || {}).label || d.provider}` : " across all providers"}.</p></div>
      <div className="toolbar-actions">{[7, 30, 90].map(n => <button key={n} className={n === days ? "primary-button" : "outline-button"} onClick={() => setDays(n)}>{n} days</button>)}</div>
    </div>
    <div className="kpi-grid kpi-grid-6">{tiles.map(([label, val, sub]) => <div key={label} className="kpi"><div className="kpi-label">{label}</div><strong>{val}</strong><small>{sub}</small></div>)}</div>
    <div className="lower-grid">
      <div className="panel"><h3>Calls per day</h3>
        <div className="bar-chart">{d.series.map(s => <div key={s.day} title={`${s.day}: ${s.calls} calls, ${s.connected} connected`} className="bar-col">
          <div className="bar-all" style={{ height: `${(s.calls / maxDay) * 100}%`, minHeight: s.calls ? 3 : 0 }}><div className="bar-hit" style={{ height: `${s.calls ? (s.connected / s.calls) * 100 : 0}%` }} /></div></div>)}</div>
        <small className="muted">Bright part = real conversations.</small></div>
      <div className="panel"><h3>How conversations ended</h3>
        <div className="outcome-bars">
          {Object.keys(d.outcomes).length === 0 && <p>No conversations yet in this period.</p>}
          {Object.entries(d.outcomes).sort((a, b) => b[1] - a[1]).map(([k, n]) => <div key={k}>
            <div className="outcome-line"><span>{OUTCOME[k] || k}</span><b>{n}</b></div>
            <div className="outcome-track"><div style={{ width: `${(n / maxOut) * 100}%` }} /></div></div>)}
        </div>
      </div>
    </div>
    <div className="panel compliance">
      <h3>Compliance record</h3>
      <p>Every queued call stores who confirmed consent and when. Download the full audit log (calls, outcomes, consent confirmation) as a spreadsheet. AI-voice calls are regulated in most countries, so keep this record.</p>
      <button className="outline-button" onClick={() => downloadCallAuditCsv().catch(() => toast.error("Download failed"))}><Download size={14} /> Download audit log (CSV)</button>
    </div>
  </div>;
}
